<template>
  <div class="sf-page">
    <v-progress-linear color="accent"
                       indeterminate
                       v-bind:active="loading" />
    <div v-if="customer != null">
      <v-snackbar color="success"
                  right
                  top
                  v-model="confirmationSnackbar">
        {{ $t('saved') }}
      </v-snackbar>
      <confirm-dialog ref="deleteDialog"
                      v-bind:cancel-button-label="$t('cancel')"
                      v-bind:confirm-button-label="$t('remove')"
                      v-bind:message="$t('confirmDeleteCustomer')"
                      v-bind:title="$t('remove')"
                      v-model="deleteDialog"
                      v-on:confirm="remove" />
      <top-header v-bind:subtitle="customer.id"
                  v-bind:title="customer.companyName | defaultValue($t('untitledCustomer'))">
        <v-btn class="sf-back-btn"
               exact
               slot="left-button"
               text
               v-bind:to="{ name: 'administration-customers' }">
          <v-icon left>ti-arrow-left</v-icon>
          {{ $t('back') }}
        </v-btn>
        <template slot="right-button">
          <v-btn color="primary"
                 outlined
                 v-bind:loading="exportLoading"
                 v-on:click="exportCsv">
            <v-icon left>ti-file-spreadsheet</v-icon>
            {{ $t('exportOrders') }}
          </v-btn>
          <v-btn color="primary"
                 depressed
                 exact
                 v-bind:disabled="!hasChanges"
                 v-on:click="updateCustomer">
            <v-icon left>ti-check</v-icon>
            {{ $t('save') }}
          </v-btn>
        </template>
        <v-menu offset-y
                left
                slot="menu">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon
                   v-bind="attrs"
                   v-bind:title="$t('actions')"
                   v-on="on">
              <v-icon>ti-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-on:click="deleteDialog = true">
              <v-list-item-icon class="mr-3"><v-icon color="error">ti-trash</v-icon></v-list-item-icon>
              <v-list-item-title class="error--text">{{ $t('remove') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-tabs class="sf-tabs"
                slot="tabs"
                v-model="tabs">
          <v-tab href="#common">{{ $t('common') }}</v-tab>
          <v-tab href="#orders">{{ $t('orders') }}</v-tab>
        </v-tabs>
      </top-header>
      <div class="sf-container">
        <v-alert class="sf-alert"
                 text
                 type="error"
                 v-model="validationAlert">
          {{ $t('pleaseCompleteTheForm') }}
        </v-alert>
        <v-tabs-items v-model="tabs">
          <v-tab-item value="common">
            <v-row>
              <v-col cols="12"
                     lg="6">
                <customer-edit-card ref="customerEditCard"
                                    v-bind:customer="customer"
                                    v-bind:edit-mode="true" />
              </v-col>
              <v-col cols="12"
                     lg="6">
                <customer-invitation-edit-card class="mb-6"
                                               ref="customerInvitationEditCard"
                                               v-bind:customer="customer" />
                <customer-additional-guests-edit-card ref="customerAdditionalGuestsEditCard"
                                                      v-bind:customer="customer" />
              </v-col>
            </v-row>
          </v-tab-item>
          <v-tab-item value="orders">
            <order-summary-card v-bind:items-per-page="20"
                                v-bind:orders="orderList"
                                v-bind:is-admin="true"
                                v-on:go-to-supplier="goToSupplier" />
          </v-tab-item>
        </v-tabs-items>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  import CustomerAdditionalGuestsEditCard from '@/components/administration/CustomerAdditionalGuestsEditCard'
  import CustomerEditCard from '@/components/administration/CustomerEditCard'
  import CustomerInvitationEditCard from '@/components/administration/CustomerInvitationEditCard'
  import OrderSummaryCard from '@/components/OrderSummaryCard'
import ConfirmDialog from '@/components/common/ConfirmDialog'
  import TopHeader from '@/components/common/TopHeader'

  export default {
    async beforeRouteUpdate(to, from, next) {
      const id = to.params.id

      await this.get(id)

      next()
    },
    components: {
      ConfirmDialog,
      CustomerAdditionalGuestsEditCard,
      CustomerEditCard,
      CustomerInvitationEditCard,
      OrderSummaryCard,
      TopHeader
    },
    computed: {
      orderList() {
        return [...this.orders].filter(o => o.quantity > 0)
      },
      ...mapGetters([
        'orders'
      ])
    },
    async created() {
      const id = this.$route.params.id

      await this.get(id)
    },
    data: () => ({
      confirmationSnackbar: false,
      deleteDialog: false,
      hasChanges: false,
      id: null,
      customer: null,
      tabs: null,
      loading: false,
      validationAlert: false,
      exportLoading: false
    }),
    methods: {
      async remove() {
        await this.$store.dispatch('deleteCustomer', { id: this.id })

        this.$router.push({ name: 'administration-customers' })
      },
      async goToSupplier(supplierId) {
        await this.$store.dispatch('setCurrentCustomer', { customer: this.customer })
        this.$router.push({ name: 'supplier', params: { id: supplierId } })
      },
      async get(id) {
        this.id = id

        this.loading = true

        await this.$store.dispatch('loadCustomers')
        await this.$store.dispatch('loadPricesForCustomer', { id })
        await this.$store.dispatch('loadOrdersForCustomer', { id })

        this.loading = false

        this.customer = Object.assign({}, this.$store.getters.customer(id))
      },
      async exportCsv() {
        this.exportLoading = true

        const csv = await this.$store.dispatch('exportOrdersForCustomer', { customerId: this.id })

        let element = document.createElement('a')
        element.setAttribute('href', 'data:text/csv;charset=utf-8,%EF%BB%BF' + encodeURI(csv))
        element.setAttribute('download', 'export.csv')
        element.style.display = 'none'

        document.body.appendChild(element)

        element.click();

        document.body.removeChild(element)

        this.exportLoading = false
      },
      async updateCustomer() {
        if (!this.$refs.customerEditCard.validate() || !this.$refs.customerInvitationEditCard.validate() || !this.$refs.customerAdditionalGuestsEditCard.validate()) {
          this.validationAlert = true
          this.$vuetify.goTo(0)

          return
        }

        const saved = await this.$store.dispatch('updateCustomer', { customer: this.customer })

        if (saved != null) {
          // Server hat ggf. neue Gastcodes vergeben: Anzeige aktualisieren, ohne sie als Änderung zu werten
          this.customer = Object.assign({}, saved)
          await this.$nextTick()
        }

        this.confirmationSnackbar = true
        this.hasChanges = false
      }
    },
    name: 'Customer',
    watch: {
      customer: {
        deep: true,
        handler(newCustomer, oldCustomer) {
          if (newCustomer == null || oldCustomer == null) {
            this.hasChanges = false
          } else if (newCustomer.id !== oldCustomer.id) {
            this.hasChanges = false
          } else {
            this.hasChanges = true
          }
        }
      }
    }
  }
</script>

<i18n>
  {
    "de": {
      "common": "Allgemein",
      "back": "Zurück",
      "exportOrders": "Bestellungen exportieren",
      "orders": "Bestellungen",
      "pleaseCompleteTheForm": "Bitte vervollständigen Sie Ihre Angaben.",
      "save": "Speichern",
      "saved": "Gespeichert.",
      "untitledCustomer": "Unbenannter Kunde",
      "actions": "Aktionen",
      "cancel": "Abbrechen",
      "remove": "Löschen",
      "confirmDeleteCustomer": "Möchten Sie diesen Kunden endgültig löschen? Dies kann nicht rückgängig gemacht werden."
    },
    "en": {
      "common": "Allgemein",
      "back": "Back",
      "exportOrders": "Export orders",
      "orders": "Bestellungen",
      "pleaseCompleteTheForm": "Please complete the form.",
      "save": "Save",
      "saved": "Saved.",
      "untitledCustomer": "Unitled customer",
      "actions": "Actions",
      "cancel": "Cancel",
      "remove": "Delete",
      "confirmDeleteCustomer": "Do you really want to delete this customer? This cannot be undone."
    }
  }
</i18n>